import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';

/**
 * True when the Migration Candidate's own lifecycle `status` claims a link to
 * a Business Partner exists — `'matched'` (import auto-linked, an Exact
 * Match) or `'accepted'` (the Dealer Merged it, this session or an earlier
 * one). Shared by {@link needsConnect} and {@link hasBrokenLink} so the one
 * `status` test they both depend on can't drift between them.
 */
function claimsLink(suggestion: MigrationCandidateBusinessPartnerBody): boolean {
  return suggestion.status === 'matched' || suggestion.status === 'accepted';
}

/**
 * A Customer Suggestion that is already linked to a Business Partner. The
 * Connect wizard needs that Business Partner (its id is the Service Agreement's
 * `customerBusinessPartnerId`), so it takes this narrowed type rather than
 * re-checking at every use.
 */
export type LinkedCustomerSuggestion = MigrationCandidateBusinessPartnerBody & {
  businessPartner: NonNullable<MigrationCandidateBusinessPartnerBody['businessPartner']>;
};

/**
 * The candidate is linked to a Business Partner but the Service Agreement is
 * still pending — the Connect wizard's precondition, and the whole rule for the
 * Merge hand-off: whatever `status` the accept response echoes back, a pending
 * Service Agreement means the dealer must still be walked through Connect.
 *
 * Narrows to {@link LinkedCustomerSuggestion} so the compiler carries the linked
 * Business Partner into the wizard.
 *
 * The generated type's doc comment for `serviceAgreementStatus` used to claim
 * it is "NULL if status != matched", which backend confirmed was inaccurate
 * (see `docs/adr/0004-*.md`): an `accepted` candidate can carry a non-null
 * `serviceAgreementStatus` too, which is exactly the shape {@link needsConnect}
 * depends on. Backend has since corrected the spec — `serviceAgreementStatus`
 * is now a required field rather than optional/nullable — so this is no
 * longer just an undocumented runtime behavior; the routing logic below
 * matches the (now-accurate) spec.
 */
export function needsServiceAgreement(
  suggestion: MigrationCandidateBusinessPartnerBody
): suggestion is LinkedCustomerSuggestion {
  return !!suggestion.businessPartner && suggestion.serviceAgreementStatus === 'pending';
}

/**
 * Decides whether Review should skip match selection and go straight to the
 * Connect wizard — i.e. whether this row's own next step is Connect, not
 * Review. Full rule and rationale: `docs/adr/0004-connect-routing-reads-migration-candidate-status.md`.
 *
 * Deliberately reads {@link claimsLink} (`status`), not `matchDetails` in
 * either direction: the backend does not clear `top_targets` once a
 * candidate transitions out of `pending`, so an `accepted` row can still
 * carry its original ranked matches — `top_targets` being empty is not a
 * reliable "this is resolved" signal, only `status` is.
 */
export function needsConnect(
  suggestion: MigrationCandidateBusinessPartnerBody
): suggestion is LinkedCustomerSuggestion {
  return claimsLink(suggestion) && needsServiceAgreement(suggestion);
}

/**
 * Guards against a should-never-happen shape the current API contract does
 * not actually rule out: `status` claims the candidate is linked (see
 * {@link claimsLink}) but no `businessPartner` came back with it.
 *
 * There is no correct button to render for this row — Review has nothing to
 * Merge against (the API says it's already linked) and Connect has no
 * Business Partner to create a Service Agreement for — so callers should
 * filter these rows out of the list entirely rather than routing them to
 * either dead end.
 *
 * Deliberately narrower than "no `businessPartner`" alone: a `status:
 * 'pending'` row with no `businessPartner` is the ordinary, common shape of a
 * still-open Customer Suggestion and is a normal Review row, not broken.
 */
export function hasBrokenLink(suggestion: MigrationCandidateBusinessPartnerBody): boolean {
  return claimsLink(suggestion) && !suggestion.businessPartner;
}
